import { createAction, handleActions, Reducer } from 'redux-actions';
import { Tracker } from '@api/types';
import { TraceStatus } from '@redux/types';

export interface TrackerState {
  isLoading: boolean;
  trackerList: Tracker[];
  searchTerm: string;
}

export const initialState: TrackerState = {
  isLoading: false,
  trackerList: [],
  searchTerm: '',
};

type TrackerReducer<T = TrackerState> = Reducer<TrackerState, T>;

// Actions

export const TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED = 'tracker/TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED';
export const TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED = 'tracker/TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED';
export const TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED = 'tracker/TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED';
export const TRACKER_TOGGLE = 'tracker/TRACKER_TOGGLE';
export const TRACKER_TOGGLE_ALL = 'tracker/TRACKER_TOGGLE_ALL';
export const TRACKER_SEARCH = 'tracker/TRACKER_SEARCH';
export const RESET_STATE = 'tracker/RESET_STATE';

// Action creators

export const fetchTrackerListWithAccessDetails = createAction(TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED);
export const fetchTrackerListWithAccessDetailsSucceed = createAction(TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED);
export const fetchTrackerListWithAccessDetailsFailed = createAction(TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED);
export const toggleTracker = createAction(TRACKER_TOGGLE);
export const toggleAllTrackers = createAction(TRACKER_TOGGLE_ALL);
export const searchTrackerList = createAction(TRACKER_SEARCH);
export const resetState = createAction(RESET_STATE);

// Reducers

const handleFetchTrackerListWithAccessDetails: TrackerReducer = state => ({
  ...state,
  isLoading: true,
});

const handleFetchTrackerListWithAccessDetailsSucceed: TrackerReducer<Tracker[]> = (state, { payload }) => ({
  ...state,
  isLoading: false,
  trackerList: payload || [],
});

const handleToggleTracker: TrackerReducer<Tracker> = (state, { payload }) => {
  const trackerList = state.trackerList.map(tracker => {
    if (tracker.token === payload.token) {
      return { ...tracker, selected: !tracker.selected };
    }

    return tracker;
  });

  return {
    ...state,
    trackerList,
  };
};

const handleToggleAllTrackers: TrackerReducer = state => {
  const toggleState = state.trackerList.length > 0 && state.trackerList.every(partner => partner.selected);

  return {
    ...state,
    trackerList: state.trackerList.map(tracker => ({ ...tracker, selected: !toggleState })),
  };
};

const handleSearchTracker: TrackerReducer<string> = (state, { payload }) => ({
  ...state,
  searchTerm: payload || '',
});

const handleFetchTrackerListWithAccessDetailsFailed: TrackerReducer<any[]> = state => ({
  ...state,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.contact-support'),
  },
  isLoading: false,
});

const handleResetState = () => ({
  ...initialState,
});

export default handleActions<TrackerState, any>(
  {
    [TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED]: handleFetchTrackerListWithAccessDetails,
    [TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED]: handleFetchTrackerListWithAccessDetailsSucceed,
    [TRACKER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED]: handleFetchTrackerListWithAccessDetailsFailed,
    [TRACKER_TOGGLE]: handleToggleTracker,
    [TRACKER_TOGGLE_ALL]: handleToggleAllTrackers,
    [TRACKER_SEARCH]: handleSearchTracker,
    [RESET_STATE]: handleResetState,
  },
  initialState,
);

// Selectors

export const getTrackerState = (state): TrackerState => state.tracker;
